import { Chart } from "components/DataExplorationV2/Chart";
import {
  ConfigurableChartAction,
  DataExplorationChart
} from "reducers/configurableChartReducer";
import { Axis, getColorIndex, ReferenceLine } from "interfaces/chart";
import { styled } from "@mui/material/styles";
import { COLOR_PALETTE } from "core/chart-mappers/aihub-chart-to-highcharts";
import { ChartMenu } from "components/DataExplorationV2/ChartMenu";
import React from "react";
import {
  createSiteAverage,
  createSiteRange
} from "core/data-exploration-v2-utils";

export interface ConfigurableChartProps {
  chart: DataExplorationChart;
  dispatchChartUpdate?: React.Dispatch<ConfigurableChartAction>;
}

/**
 * A component which displays a chart and menu. The menu allows the user to update the chart.
 * @param chart the current state of the chart
 * @param dispatchChartUpdate a dispatch function which can be used to update the chart using the available actions.
 */
export const ConfigurableChart = ({
  chart,
  dispatchChartUpdate = () => {}
}: ConfigurableChartProps): JSX.Element => {
  const handleAddSiteAverageToChart = (
    parentId: string,
    averageSeriesId: string,
    title: string,
    tagId: number,
    siteId: number,
    pointsEnabled: boolean,
    xAxisId: string,
    yAxisId: string
  ): void => {
    const colorIndex = getColorIndex(chart.series, COLOR_PALETTE.length);
    const siteAverage = createSiteAverage(
      parentId,
      averageSeriesId,
      title,
      tagId,
      siteId,
      xAxisId,
      yAxisId,
      colorIndex,
      pointsEnabled
    );
    dispatchChartUpdate({
      type: "addSiteAverage",
      series: siteAverage
    });
  };

  const handleAddSiteRangeToChart = (
    parentId: string,
    rangeSeriesId: string,
    title: string,
    tagId: number,
    siteId: number,
    xAxisId: string,
    yAxisId: string
  ): void => {
    const colorIndex = getColorIndex(chart.series, COLOR_PALETTE.length);
    const siteRange = createSiteRange(
      parentId,
      rangeSeriesId,
      title,
      tagId,
      siteId,
      xAxisId,
      yAxisId,
      colorIndex
    );
    dispatchChartUpdate({ type: "addSiteRange", series: siteRange });
  };

  return (
    <>
      <Wrapper>
        <Chart chartData={chart} />
        <ChartMenu
          chart={chart}
          onChangePointsEnabled={pointsEnabled =>
            dispatchChartUpdate({ type: "changePointsEnabled", pointsEnabled })
          }
          onChangeTitle={title =>
            dispatchChartUpdate({ type: "changeTitle", title })
          }
          onAddSeries={(seriesId, pointsEnabled) =>
            dispatchChartUpdate({ type: "addSeries", seriesId, pointsEnabled })
          }
          onChangeMachineOnSeries={(seriesId, title, machineId, tagId) =>
            dispatchChartUpdate({
              type: "changeMachineOnSeries",
              seriesId,
              title,
              machineId,
              tagId
            })
          }
          onChangeParameterOnSeries={(
            seriesId,
            title,
            machineId,
            tagId,
            tagName
          ) => {
            dispatchChartUpdate({
              type: "changeTagOnSeries",
              seriesId,
              title,
              tagId,
              tagName,
              machineId
            });
          }}
          onAddSiteAverage={handleAddSiteAverageToChart}
          onAddSiteRange={handleAddSiteRangeToChart}
          onUpdateSiteAverage={(seriesId, title, siteId, tagId) =>
            dispatchChartUpdate({
              type: "updateSiteAverage",
              seriesId,
              title,
              siteId,
              tagId
            })
          }
          onUpdateSiteRange={(seriesId, title, siteId, tagId) =>
            dispatchChartUpdate({
              type: "updateSiteRange",
              seriesId,
              title,
              siteId,
              tagId
            })
          }
          onChangeXAxisLabelEnabled={xAxisEnabled =>
            dispatchChartUpdate({
              type: "changeXAxesTitleEnabled",
              xAxisEnabled
            })
          }
          onChangeStartDate={startDate =>
            dispatchChartUpdate({
              type: "changeXAxesMin",
              min: startDate?.toString()
            })
          }
          onChangeEndDate={endDate =>
            dispatchChartUpdate({
              type: "changeXAxesMax",
              max: endDate?.toString()
            })
          }
          onChangeYAxisMin={(yAxisId, min) =>
            dispatchChartUpdate({
              type: "changeYAxisMin",
              yAxisId,
              min
            })
          }
          onChangeYAxisMax={(yAxisId, max) =>
            dispatchChartUpdate({ type: "changeYAxisMax", yAxisId, max })
          }
          onChangeYAxisLabelEnabled={yAxisId =>
            dispatchChartUpdate({ type: "toggleYAxisLabel", yAxisId })
          }
          onDeleteSeries={(...seriesIds) =>
            dispatchChartUpdate({ type: "deleteMultipleSeries", seriesIds })
          }
          onDeleteAllSeries={() =>
            dispatchChartUpdate({ type: "deleteAllSeries" })
          }
          onResetXAxesRange={() =>
            dispatchChartUpdate({ type: "resetXAxesRange" })
          }
          onChangeReferenceLineAxis={(
            axis: Axis | null,
            selectedReferenceLineId
          ) =>
            dispatchChartUpdate({
              type: "changeReferenceLineAxis",
              selectedReferenceLineId,
              axis
            })
          }
          onChangeReferenceLineValue={(
            value: string | number | Date | null,
            selectedReferenceLineId
          ) =>
            dispatchChartUpdate({
              type: "changeReferenceLineValue",
              selectedReferenceLineId,
              value
            })
          }
          onAddReferenceLine={(newId: string) =>
            dispatchChartUpdate({
              type: "addReferenceLine",
              newId
            })
          }
          onDeleteReferenceLine={(referenceLineId: string) =>
            dispatchChartUpdate({
              type: "deleteReferenceLine",
              referenceLineId
            })
          }
          onChangeReferenceLineTitle={(
            referenceLine: ReferenceLine,
            title: string | null
          ) =>
            dispatchChartUpdate({
              type: "changeReferenceLineTitle",
              referenceLine,
              title
            })
          }
        />
      </Wrapper>
    </>
  );
};

const Wrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 400px;
`;
